import { useState } from 'react';

const faqs = [
  {
    q: 'Do I need to know anything about copywriting or design to use AdForge?',
    a: 'Not at all. AdForge handles both the writing and the visuals. You fill in a short form about your business and offer — AdForge generates the hooks, headlines, body copy, CTAs, and AI creative images automatically. No copywriting or design experience needed.',
  },
  {
    q: 'What ad platforms does AdForge support?',
    a: 'AdForge generates ad copy and creatives optimized for Facebook, Instagram, Google Ads, TikTok, and LinkedIn. Each output is formatted to match the specific requirements and best practices of that platform.',
  },
  {
    q: 'What does AdForge actually generate — just copy, or images too?',
    a: 'Both. Every generation produces AI-written copy (hooks, headlines, body copy, CTAs) plus AI-generated creative images — all in one go. Free plan gets 1 creative image per generation, Pro gets 3, and Agency gets 6. You can also regenerate individual images without re-running the whole campaign.',
  },
  {
    q: 'How many ads can I generate on the free plan?',
    a: 'The free plan includes 25 credits per month. A standard ad costs 10 credits, a social post costs 3 credits, and adding an AI image to a social post costs 5 more. Upgrade to Pro (250 credits) or Agency (1,000 credits) for more.',
  },
  {
    q: 'What is the Visual Ad Editor?',
    a: 'The Visual Editor lets you take any AI-generated ad and customize it without any design tools. You can drag and reposition elements, edit text inline, customize button styles, adjust colors and opacity, and fine-tune animations — all inside AdForge. Just click "Open in Visual Editor" after generating.',
  },
  {
    q: 'Can I edit the generated images?',
    a: 'Yes. Every ad image gets 2 free edits. After that, edits cost 2 credits each. You can also use the Visual Editor to overlay and adjust text, shapes, and buttons on top of the generated image. Or hit the regenerate button to swap out a single image without re-running the full campaign.',
  },
  {
    q: 'Is the copy unique, or will other businesses get the same ads?',
    a: 'Every ad is generated uniquely based on your specific inputs — your business name, offer, audience, tone, ad angle, and campaign goal. No two businesses share the same combination, so your copy and creative will be one-of-a-kind.',
  },
  {
    q: 'Will AdForge work for my type of business?',
    a: 'AdForge works for almost any business: contractors, restaurants, e-commerce, fitness, legal, real estate, SaaS, consulting, and more. If you have an offer and an audience, AdForge can write and design ads for it.',
  },
  {
    q: 'Can I cancel my subscription at any time?',
    a: 'Yes, always. There are no long-term contracts or cancellation fees. Cancel your Pro or Agency plan at any time from your account settings and you\'ll keep access until the end of your billing period.',
  },
];

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block text-sm font-semibold text-orange-500 uppercase tracking-widest mb-3">FAQ</span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-stone-900 mb-4">
            Questions? We've got answers.
          </h2>
          <p className="text-lg text-stone-500">
            Still have questions? Email us at{' '}
            <a href="mailto:support@adforgeai.app" className="text-orange-500 hover:underline">support@adforgeai.app</a>
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className={`rounded-2xl border transition-all duration-200 ${
                openIdx === idx ? 'border-orange-200 bg-orange-50/50' : 'border-stone-100 bg-stone-50 hover:border-stone-200'
              }`}
            >
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
              >
                <span className={`text-base font-semibold ${openIdx === idx ? 'text-orange-600' : 'text-stone-800'}`}>
                  {faq.q}
                </span>
                <span className={`w-6 h-6 flex items-center justify-center rounded-full shrink-0 ml-4 transition-all duration-200 ${
                  openIdx === idx ? 'bg-orange-500 text-white rotate-180' : 'bg-stone-200 text-stone-500'
                }`}>
                  <i className="ri-arrow-down-s-line text-sm" />
                </span>
              </button>
              {openIdx === idx && (
                <div className="px-6 pb-5">
                  <p className="text-stone-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
